import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { countStacksUse } from '@pubstack/common/src/stack'
import { FunctionComponent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useArchiveBidderRule, useBidderRules } from '~/api/bidder-rules.api'
import { Colors } from '~/assets/style/colors'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PageContent } from '~/components/PageContent'
import { Pagination } from '~/components/Pagination'
import Table, { handleTableSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { ArchiveRuleModal } from '~/feature-components/ArchiveRuleModal'
import { RuleListToolbar } from '~/feature-components/RuleListToolbar'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useClientPagination } from '~/hooks/useClientPagination'
import { byRuleName, byStackCount, stackCountOf } from './bidderRuleSort'
import { BIDDER_RULES_PATH } from './bidderRulesPath'
import { useBidderRuleApiProduct } from './useBidderRuleCapabilities'
import { ViewBidderRuleFlyout } from './ViewBidderRuleFlyout'

const FLYOUT_WIDTH = '600px'

export const BidderRulesPage: FunctionComponent = () => {
  const [columns, setColumns] = useState<TableColumns<BidderRule>>([
    {
      name: 'Rule',
      isSortable: true,
      order: 'none',
      attributeSort: 'name',
      sort: byRuleName,
    },
    {
      name: 'Used by',
      isSortable: true,
      order: 'descending',
      attributeSort: 'usedInStacks',
      sort: byStackCount,
    },
    { name: 'Actions', isSortable: false },
  ])

  const [search, setSearch] = useState('')

  const apiProduct = useBidderRuleApiProduct()
  const { data: bidderRules, isLoading } = useBidderRules(apiProduct, true)
  const { mutateAsync: archiveBidderRule, isPending: isArchiving } = useArchiveBidderRule(apiProduct, {
    successToast: 'Bidder rule archived',
    errorToast: 'Could not archive this bidder rule',
  })

  const modal = useGlobalModal()
  const breadcrumbs = useBreadcrumbs()
  /* Creating and editing a rule are owner-only at the API — a rule is shared, so changing one moves every
     stack pointing at it. Reading the list stays open. */
  const { canEdit, canArchive, disabledReason } = useScopePermissions()
  const flyout = useGlobalFlyout()
  const navigate = useNavigate()

  const sortedBidderRules = useMemo(() => handleTableSort(columns, bidderRules).filter((bidderRule) => bidderRule.name.toLowerCase().includes(search.toLowerCase())), [columns, bidderRules, search])
  const { pageItems, paginationProps, resetPage } = useClientPagination(sortedBidderRules)

  /**
   * Why this row cannot be archived, or `undefined` when it can.
   *
   * The same two refusals the API makes, said before the click rather than after it. A default is the
   * rule a stack falls back to, and a rule any stack still points at would be pulled out from under it.
   */
  const archiveDisabledReason = (bidderRule: BidderRule): string | undefined => {
    if (bidderRule.isDefault) {
      return "Can't archive a default rule"
    }
    const stackCount = countStacksUse(bidderRule.usedInStacks)
    if (stackCount > 0) {
      return `Used by ${stackCount} stack${stackCount === 1 ? '' : 's'}. Remove it from them first.`
    }
    return canArchive ? undefined : disabledReason
  }

  const handleArchiveClick = (e: React.MouseEvent, bidderRule: BidderRule) => {
    e.stopPropagation()
    e.preventDefault()
    modal.open(ArchiveRuleModal, {
      name: bidderRule.name,
      noun: 'bidder rule',
      onArchive: () => archiveBidderRule({ id: bidderRule.id, archived: true }),
      isPending: isArchiving,
      trackingPrefix: 'common-bidder-rules-archive',
    })
  }

  const handleNewClick = () => navigate(`${BIDDER_RULES_PATH}/new`)

  const handleEditClick = (e: React.MouseEvent, bidderRule: BidderRule) => {
    e.stopPropagation()
    e.preventDefault()
    navigate(`${BIDDER_RULES_PATH}/edit/${bidderRule.id}`)
  }

  const handleViewClick = (e: React.MouseEvent, bidderRule: BidderRule) => {
    e.stopPropagation()
    e.preventDefault()
    flyout.open(
      ViewBidderRuleFlyout,
      { color: Colors.Ming, width: FLYOUT_WIDTH, closeTrackingLabel: 'common-bidder-rules-close' },
      {
        bidderRule,
        /* The row's own pen, from the flyout the row opens: reading a rule is how a publisher decides to
           change it, and without this the way from one to the other was to close the flyout and find the
           row again. Straight to the form, unlike the same button inside the stack form, which confirms
           first — there the rule is one a stack merely picked, and editing it moves every other stack on
           it too. */
        onEdit: () => {
          flyout.close()
          navigate(`${BIDDER_RULES_PATH}/edit/${bidderRule.id}`)
        },
        isEditable: canEdit,
      }
    )
  }

  return (
    /* No `headerActions`: the title bar is the title and nothing else on every rule list now. Creating a
       rule acts on *this list*, so it belongs on the list's own row beside the rest of what is done to
       it — which is where the medias and ad units lists have long put it. */
    <PageContent breadcrumbs={breadcrumbs}>
      <RuleListToolbar
        search={search}
        onSearchChange={(next) => {
          setSearch(next)
          resetPage()
        }}
        createLabel={'New rule'}
        onCreate={handleNewClick}
        createTrackingLabel={'common-bidder-rules-new'}
        canCreate={canEdit}
        createDisabledReason={disabledReason}
        canSeeArchived={canArchive}
        archivedLabel={'Archived rules'}
        onNavigateToArchived={() => navigate(`${BIDDER_RULES_PATH}/archived`)}
        archivedTrackingLabel={'common-bidder-rules-archived-navigate'}
        actionsTrackingLabel={'common-bidder-rules-actions-view'}
      />
      {/* A search that matches nothing is still a populated list: it gets the table and its "No results"
          row, where a scope with no rules at all gets the illustration inviting it to make one. */}
      {isLoading || bidderRules.length > 0 ? (
        <>
          <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
            {pageItems.length > 0 ? (
              pageItems.map((bidderRule) => (
                <TableRow key={bidderRule.id} clickable={{ onClick: (e) => handleViewClick(e, bidderRule), trackingLabel: 'common-bidder-rules-view' }}>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      {bidderRule.name}
                      {bidderRule.isDefault && (
                        <Tooltip title="The rule a stack falls back to. It can be edited, but not archived.">
                          <span role="img" aria-label="Default rule" className="flex">
                            <Icon name="star" width="16px" height="16px" fill={'var(--color-king)'} />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{`${stackCountOf(bidderRule)} stack${stackCountOf(bidderRule) === 1 ? '' : 's'}`}</TableCell>
                  <ActionsTableCell>
                    <ActionButtons>
                      <Tooltip title="View">
                        <Button iconName="view" variant="tertiary" title="View" onClick={(e) => handleViewClick(e, bidderRule)} trackingLabel="common-bidder-rules-icon-view" />
                      </Tooltip>
                      <Tooltip title={canEdit ? 'Edit' : disabledReason} align="end">
                        <Button iconName="edit" variant="tertiary" title="Edit" onClick={(e) => handleEditClick(e, bidderRule)} trackingLabel="common-bidder-rules-edit" disabled={!canEdit} />
                      </Tooltip>
                      {/* Owners only, and only for a rule nothing uses — the button stays on the row saying
                        which of the two it is, rather than disappearing and leaving the reader to guess
                        whether archiving exists at all. */}
                      {canArchive && (
                        <Tooltip title={archiveDisabledReason(bidderRule) ?? 'Archive'} align="end">
                          <Button
                            iconName="archive"
                            variant="tertiary"
                            title="Archive"
                            onClick={(e) => handleArchiveClick(e, bidderRule)}
                            trackingLabel="common-bidder-rules-archive"
                            disabled={!!archiveDisabledReason(bidderRule) || isArchiving}
                          />
                        </Tooltip>
                      )}
                    </ActionButtons>
                  </ActionsTableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <NoResultsTableCell colspan={columns.length}>No results</NoResultsTableCell>
              </TableRow>
            )}
          </Table>
          <Pagination {...paginationProps} className={'mt-4'} />
        </>
      ) : (
        <IllustrationMessage iconName="night_sky" iconSize="190px" title="It seems that no bidder rules have been created.">
          <Tooltip title={disabledReason} disabled={canEdit}>
            <Button variant="primary" disabled={!canEdit} onClick={handleNewClick} trackingLabel="common-bidder-rules-new">
              New rule
            </Button>
          </Tooltip>
        </IllustrationMessage>
      )}
    </PageContent>
  )
}
